import axios from 'axios';
import { ensureFreshMicrosoftToken } from '../auth/msal';
import { config } from '../config/environment';

// AitryxCore token management for partner portal
// Follows the same pattern as customer app for enterprise-grade security

let cachedAitryxCoreToken: string | null = null;
let coreTokenExpiresAt: number | null = null; // epoch ms

function isExpiringSoon(expiresAtMs: number | null, bufferSeconds: number = 300): boolean {
  if (!expiresAtMs) return true;
  const now = Date.now();
  return now + bufferSeconds * 1000 >= expiresAtMs;
}

/**
 * Ensures we have a fresh AitryxCore JWT token for secure API communication
 * Follows the same enterprise-grade pattern as customer app:
 * 1. Get Azure AD token
 * 2. Exchange for AitryxCore JWT via /api/user/issue-core-token
 * 3. Use AitryxCore JWT for all subsequent API calls
 */
export async function ensureFreshAitryxCoreToken(): Promise<string> {
  if (cachedAitryxCoreToken && !isExpiringSoon(coreTokenExpiresAt)) {
    return cachedAitryxCoreToken;
  }

  try {
    // First, get a fresh Azure AD token
    const microsoftToken = await ensureFreshMicrosoftToken();
    
    // Exchange Azure AD token for AitryxCore JWT via /api/user/issue-core-token
    // CRITICAL: Use a raw axios instance (not apiService) to avoid circular dependency
    // The apiService interceptor would try to get AitryxCore token, creating infinite loop
    const response = await axios.post(
      `${config.api.baseUrl}/user/issue-core-token`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${microsoftToken}`,
          'Content-Type': 'application/json'
        },
        timeout: config.api.timeout
      }
    );
    const data = response.data || {};

    const accessToken: string | undefined = data.accessToken || data.data?.accessToken;
    const expiresAt: string | undefined = data.expiresAt || data.data?.expiresAt;

    if (!accessToken) {
      throw new Error('Failed to obtain AitryxCore JWT token');
    }

    cachedAitryxCoreToken = accessToken;
    if (expiresAt) {
      // expiresAt is ISO string; convert to ms
      const ts = Date.parse(expiresAt);
      coreTokenExpiresAt = isNaN(ts) ? null : ts;
    } else {
      // Fallback: assume 55 minutes validity (same as customer app)
      coreTokenExpiresAt = Date.now() + 55 * 60 * 1000;
    }

    return cachedAitryxCoreToken;

  } catch (error) {
    throw new Error('Failed to obtain AitryxCore JWT token. Please sign in again.');
  }
}

/**
 * Clear the cached AitryxCore token (for logout scenarios)
 */
export function clearAitryxCoreTokenCache(): void {
  cachedAitryxCoreToken = null;
  coreTokenExpiresAt = null;
}

/**
 * Get current cached token (for debugging)
 */
export function getCurrentTokenInfo(): { hasToken: boolean; expiresAt: string | null } {
  return {
    hasToken: !!cachedAitryxCoreToken,
    expiresAt: coreTokenExpiresAt ? new Date(coreTokenExpiresAt).toISOString() : null
  };
}
